/**
 * @fileoverview Workflows manager (MOR-696, renamed MOR-716)
 *
 * Lists an org's reusable workflows in a table and hosts the create /
 * duplicate / delete / apply-to-job / AI-import actions. Editing a workflow's
 * tasks happens on the dedicated editor sub-route. Admin/owner only — `canEdit`
 * gates writes; the backend mutations enforce the same rule.
 */

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Copy,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Badge, Button } from "@/ui/primitives";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import { APPOINTMENT_TYPE_TAGS } from "@/lib/precedent-templates";
import { extractErrorMessage } from "@/utils/errors";
import { WorkflowImportDialog } from "./WorkflowImportDialog";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

const APPLY_MODE_LABEL: Record<string, string> = {
  manual: "Manual",
  all_jobs: "All jobs",
  by_type: "By type",
};

const TYPE_LABEL = new Map(
  APPOINTMENT_TYPE_TAGS.map((t) => [t.value, t.label]),
);

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function WorkflowsManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const navigate = useNavigate();
  const { data: templates, isPending } = useQuery(
    convexQuery(api.tasks.workflows.listByOrg, { orgId }),
  );
  const createTemplate = useConvexMutation(api.tasks.workflows.create);
  const duplicateTemplate = useConvexMutation(api.tasks.workflows.duplicate);
  const archiveTemplate = useConvexMutation(api.tasks.workflows.archive);

  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    id: Id<"taskTemplates">;
    name: string;
  } | null>(null);

  const rows = templates ?? [];

  const goToEditor = (templateId: Id<"taskTemplates">) =>
    navigate({
      to: "/dashboard/workflows/$workflowId",
      params: { workflowId: templateId },
    });

  const handleNew = async () => {
    setBusy(true);
    try {
      const id = await createTemplate({ orgId, name: "Untitled workflow" });
      await goToEditor(id);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not create the workflow."));
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async (templateId: Id<"taskTemplates">) => {
    setBusy(true);
    try {
      const id = await duplicateTemplate({ templateId });
      toast.success("Workflow duplicated.");
      await goToEditor(id);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not duplicate the workflow."));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await archiveTemplate({ templateId: pendingDelete.id });
      toast.success("Workflow deleted.");
      setPendingDelete(null);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not delete the workflow."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {!canEdit && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Read-only view</p>
            <p className="mt-0.5 text-xs opacity-80">
              You need owner or admin access to manage workflows.
            </p>
          </div>
        </div>
      )}

      <div className={GROUP_CARD_CLASS}>
        <div className="flex items-start justify-between gap-6 border-b border-border p-6">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-primary">Workflows</h3>
            <p className="mt-1 text-sm text-primary/60">
              Add and manage your workflows, which are reusable sets of tasks
              &amp; events that can be applied to jobs or auto-applied on job
              creation by appointment type.
            </p>
          </div>
          {canEdit && (
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportOpen(true)}
                disabled={busy}
              >
                <Sparkles className="mr-1.5 size-4" />
                Import from spreadsheet
              </Button>
              <Button
                size="sm"
                onClick={() => void handleNew()}
                disabled={busy}
              >
                {busy ? (
                  <Loader2 className="mr-1.5 size-4 animate-spin" />
                ) : (
                  <Plus className="mr-1.5 size-4" />
                )}
                New workflow
              </Button>
            </div>
          )}
        </div>

        {isPending ? (
          <div className="flex items-center justify-center gap-2 px-6 py-16 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading workflows…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-muted-foreground">
            No workflows yet.
            {canEdit &&
              " Create one or import from a spreadsheet to get started."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-[90px] text-center">Tasks</TableHead>
                <TableHead className="w-[150px] text-center">
                  Automations applied
                </TableHead>
                <TableHead>Apply mode</TableHead>
                <TableHead className="w-[140px]">Created</TableHead>
                <TableHead className="w-[180px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow
                  key={t._id}
                  className="cursor-pointer"
                  onClick={() => void goToEditor(t._id)}
                >
                  <TableCell className="font-medium text-primary">
                    {t.name}
                    {t.description && (
                      <p className="mt-0.5 max-w-md truncate text-xs font-normal text-muted-foreground">
                        {t.description}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-muted-foreground">
                    {t.itemCount}
                  </TableCell>
                  <TableCell className="text-center">
                    {t.automationCount > 0 ? (
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <Zap className="size-3.5 text-amber-500" />
                        {t.automationCount}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge
                        variant={
                          t.applyMode === "manual" ? "secondary" : "default"
                        }
                      >
                        {APPLY_MODE_LABEL[t.applyMode] ?? t.applyMode}
                      </Badge>
                      {t.applyMode === "by_type" &&
                        t.appointmentTypes.map((ty) => (
                          <Badge key={ty} variant="outline">
                            {TYPE_LABEL.get(ty) ?? ty}
                          </Badge>
                        ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(t.createdAt)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Edit"
                            onClick={() => void goToEditor(t._id)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Duplicate"
                            disabled={busy}
                            onClick={() => void handleDuplicate(t._id)}
                          >
                            <Copy className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Delete"
                            onClick={() =>
                              setPendingDelete({ id: t._id, name: t.name })
                            }
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {importOpen && (
        <WorkflowImportDialog
          orgId={orgId}
          open={importOpen}
          onOpenChange={setImportOpen}
        />
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this workflow?</AlertDialogTitle>
            <AlertDialogDescription>
              “{pendingDelete?.name}” will be removed. Tasks already applied to
              jobs are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
